import CryptoJS from 'crypto-js';

// 获取签名函数
function signCallback(secretKey, signStr) {
    // 计算 HMAC-SHA1
    const hash = CryptoJS.HmacSHA1(signStr, secretKey);
    // 转为 hex 字符串
    const hex = hash.toString(CryptoJS.enc.Hex);
    // hex → Uint8Array
    const arr = new Uint8Array(hex.match(/.{2}/g).map(byte => parseInt(byte, 16)));
    // Uint8Array → Base64
    return btoa(String.fromCharCode(...arr));
}

// 格式化签名字符串
function formatSignString(params) {
    let strParam = "";
    let signStr = "tts.cloud.tencent.com";
    
    const keys = Object.keys(params);
    keys.sort();
    for (let i = 0, len = keys.length; i < len; i++) {
        strParam += `&${keys[i]}=${params[keys[i]]}`;
    }
    signStr = `POST${signStr}?${strParam.slice(1)}`;
    console.log(signStr);
    return signStr;
}

// 生成UUID
const guid = () => {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = Math.random() * 16 | 0,
            v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
};

export default class WebAudioTTS {
    constructor(currentHostIP, params, isLog) {
        this.currentHostIP = currentHostIP;
        this.params = params;
        this.isLog = isLog;
        this.requestId = null;
        this.audioContext = null;
        this.audioQueue = [];
        this.isPlaying = false;
        this.currentAudio = null;
        this.isStopped = false;
        this.pollingInterval = null; // 轮询定时器
        this.maxPollingAttempts = 120; // 最大轮询次数（2分钟，每次1秒）
        this.pollingAttempts = 0; // 当前轮询次数
    }

    // 开始合成语音
    async start(text, sessionId = null) {
        if (!text || text.trim() === '') {
            this.OnError('文本不能为空');
            return;
        }

        if (this.isPlaying) {
            this.OnError('正在播放音频，请稍后再试');
            return;
        }

        try {
            this.isStopped = false;
            this.pollingAttempts = 0;
            
            // 调用后端API提交TTS任务
            const response = await fetch(this.currentHostIP + '/api/tencent/tts', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({
                    text: text,
                    session_id: sessionId
                })
            });

            if (!response.ok) {
                throw new Error(`HTTP错误: ${response.status}`);
            }

            const result = await response.json();
            console.log('TTS任务提交响应:', result);
            
            if (!result.success) {
                throw new Error(result.message || 'TTS任务提交失败');
            }

            // 根据实际返回结构获取TaskId: result.data.Data.TaskId
            const taskId = result.data?.Data?.TaskId || result.data?.TaskId;
            if (!taskId) {
                console.error('无法获取TaskId，响应结构:', result);
                throw new Error('未获取到任务ID');
            }

            console.log('TTS任务已提交，TaskId:', taskId);
            
            // 开始轮询任务状态
            this.OnSynthesisStart();
            // 不等待轮询完成，让它异步执行
            this.pollTaskStatus(taskId).catch(error => {
                console.error('轮询过程中出错:', error);
                this.OnError('获取TTS结果失败: ' + error.message);
            });
            
        } catch (error) {
            console.error('TTS合成失败:', error);
            this.OnError('TTS合成失败: ' + error.message);
            this.stopPolling();
        }
    }

    // 轮询任务状态
    async pollTaskStatus(taskId) {
        if (this.isStopped) {
            console.log('轮询已停止，isStopped=true');
            return;
        }

        try {
            console.log(`开始轮询任务状态，TaskId: ${taskId}, 尝试次数: ${this.pollingAttempts + 1}`);
            
            const response = await fetch(`${this.currentHostIP}/api/tencent/tts/status/${taskId}`, {
                method: 'GET'
            });

            if (!response.ok) {
                throw new Error(`HTTP错误: ${response.status}`);
            }

            // 检查响应类型：如果是音频文件（二进制数据），直接处理
            const contentType = response.headers.get('content-type') || '';
            console.log('响应Content-Type:', contentType);
            
            // 如果是音频类型，直接处理
            if (contentType.startsWith('audio/')) {
                console.log('检测到音频响应，任务完成');
                const audioBlob = await response.blob();
                const audioUrl = URL.createObjectURL(audioBlob);
                await this.playAudioFromUrl(audioUrl);
                this.stopPolling();
                return;
            }

            // 尝试解析为JSON（任务进行中或已完成但返回JSON）
            // 使用克隆响应，以便在需要时重新读取
            const clonedResponse = response.clone();
            
            try {
                const result = await response.json();
                console.log('收到JSON响应:', result);
                
                // 检查是否完成（如果JSON中包含了音频数据）
                if (result.audio) {
                    console.log('JSON中包含音频数据');
                    // 处理base64音频数据
                    const audioBlob = this.base64ToBlob(result.audio, 'audio/wav');
                    const audioUrl = URL.createObjectURL(audioBlob);
                    await this.playAudioFromUrl(audioUrl);
                    this.stopPolling();
                    return;
                }
                
                // 检查任务状态
                if (result.status === 'completed' || result.completed) {
                    // 任务完成但没有音频数据，可能需要再次请求获取音频
                    throw new Error('任务完成但未获取到音频数据');
                }
                
                // 任务还在进行中，继续轮询
                console.log('任务进行中，继续轮询...');
            } catch (jsonError) {
                // 如果解析JSON失败，可能是二进制音频数据
                if (jsonError instanceof SyntaxError || jsonError.name === 'SyntaxError') {
                    console.log('JSON解析失败，尝试作为音频数据处理');
                    // 使用克隆的响应作为二进制数据处理
                    try {
                        const audioBlob = await clonedResponse.blob();
                        const audioUrl = URL.createObjectURL(audioBlob);
                        await this.playAudioFromUrl(audioUrl);
                        this.stopPolling();
                        return;
                    } catch (blobError) {
                        console.error('处理音频blob失败:', blobError);
                        throw new Error('无法处理音频数据: ' + blobError.message);
                    }
                } else {
                    // 其他错误，直接抛出
                    throw jsonError;
                }
            }

            // 任务还在进行中，继续轮询
            this.pollingAttempts++;
            
            if (this.pollingAttempts >= this.maxPollingAttempts) {
                throw new Error('TTS任务超时，请重试');
            }

            // 延迟后继续轮询（1秒）
            console.log(`等待1秒后继续轮询... (${this.pollingAttempts}/${this.maxPollingAttempts})`);
            this.pollingInterval = setTimeout(() => {
                this.pollTaskStatus(taskId);
            }, 1000);
            
        } catch (error) {
            console.error('轮询任务状态失败:', error);
            this.stopPolling();
            this.OnError('获取TTS结果失败: ' + error.message);
        }
    }

    // 停止轮询
    stopPolling() {
        if (this.pollingInterval) {
            clearTimeout(this.pollingInterval);
            this.pollingInterval = null;
        }
        this.pollingAttempts = 0;
    }


    // 处理wav音频数据
    processWavAudio(base64Audio) {
        try {
            // 直接返回wav格式的base64数据
            return base64Audio;
        } catch (error) {
            console.error('处理wav音频数据失败:', error);
            return null;
        }
    }

    // 从URL播放音频
    async playAudioFromUrl(audioUrl) {
        try {
            // 创建Audio对象并播放
            const audio = new Audio(audioUrl);
            this.currentAudio = audio;
            
            // 监听播放进度
            audio.ontimeupdate = () => {
                if (audio.duration) {
                    const progress = (audio.currentTime / audio.duration) * 100;
                    this.OnProgressUpdate(progress, audio.currentTime, audio.duration);
                }
            };
            
            // 监听元数据加载完成，获取总时长
            audio.onloadedmetadata = () => {
                this.OnDurationLoaded(audio.duration);
            };
            
            audio.onended = () => {
                URL.revokeObjectURL(audioUrl);
                this.isPlaying = false;
                this.currentAudio = null;
                this.OnPlaybackComplete();
            };

            audio.onerror = (error) => {
                console.error('音频播放错误:', error);
                URL.revokeObjectURL(audioUrl);
                this.isPlaying = false;
                this.currentAudio = null;
                this.OnError('音频播放失败');
            };

            this.isPlaying = true;
            await audio.play();
            this.OnPlaybackStart();
            
        } catch (error) {
            console.error('播放音频失败:', error);
            this.isPlaying = false;
            this.currentAudio = null;
            this.OnError('播放音频失败: ' + error.message);
        }
    }

    // 播放音频（兼容旧版本）
    async playAudio(audioData) {
        if (!audioData || this.isStopped) return;

        try {
            // 解码base64音频数据
            const binaryString = atob(audioData);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }

            // 创建Blob对象 - 使用wav格式
            const blob = new Blob([bytes], { type: 'audio/wav' });
            const url = URL.createObjectURL(blob);
            await this.playAudioFromUrl(url);
            
        } catch (error) {
            console.error('播放音频失败:', error);
            this.isPlaying = false;
            this.OnError('播放音频失败: ' + error.message);
        }
    }

    // 播放下一个音频
    playNextAudio() {
        if (this.audioQueue.length > 0 && !this.isStopped) {
            const nextAudio = this.audioQueue.shift();
            this.playAudio(nextAudio);
        } else {
            this.isPlaying = false;
        }
    }

    // base64转Blob
    base64ToBlob(base64, mimeType) {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        return new Blob([byteArray], { type: mimeType });
    }

    // 停止播放
    stop() {
        this.isStopped = true;
        this.stopPolling(); // 停止轮询
        
        if (this.currentAudio) {
            this.currentAudio.pause();
            this.currentAudio.currentTime = 0;
            this.currentAudio = null;
        }
        
        this.audioQueue = [];
        this.isPlaying = false;
        this.OnPlaybackStopped();
    }
    
    // 获取当前播放进度
    getCurrentProgress() {
        if (this.currentAudio && this.currentAudio.duration) {
            return (this.currentAudio.currentTime / this.currentAudio.duration) * 100;
        }
        return 0;
    }
    
    // 设置播放进度（0-1之间的值）
    setProgress(progress) {
        if (this.currentAudio && this.currentAudio.duration) {
            const newTime = Math.max(0, Math.min(progress, 1)) * this.currentAudio.duration;
            this.currentAudio.currentTime = newTime;
            // 立即更新进度显示
            if (this.currentAudio.duration) {
                const currentProgress = (this.currentAudio.currentTime / this.currentAudio.duration) * 100;
                this.OnProgressUpdate(currentProgress, this.currentAudio.currentTime, this.currentAudio.duration);
            }
        }
    }

    // 合成开始
    OnSynthesisStart() {}
    
    // 音频数据接收
    OnAudioDataReceived(audioData) {}
    
    // 合成完成
    OnSynthesisComplete(audioChunks) {
        if (audioChunks.length > 0) {
            // 合并所有音频片段
            const combinedAudio = audioChunks.join('');
            this.playAudio(combinedAudio);
        } else {
            console.log('TTS合成完成，但无音频数据');
        }
    }
    
    // 播放开始
    OnPlaybackStart() {}
    
    // 播放完成
    OnPlaybackComplete() {}
    
    // 播放停止
    OnPlaybackStopped() {}
    
    // 进度更新
    OnProgressUpdate(progress, currentTime, duration) {}
    
    // 时长加载完成
    OnDurationLoaded(duration) {}
    
    // 错误处理
    OnError(error) {}
}

// 全局注册
if (typeof window !== 'undefined') {
    window.WebAudioTTS = WebAudioTTS;
}